import Vue from "vue";
import VueRouter from "vue-router";
import NProgress from "nprogress";

import Home from "@/views/Home.vue";
import Login from "@/views/user/Login.vue";
import OAuth2 from "@/views/user/OAuth2.vue";
import SignDoc from "@/views/SignDoc.vue";
import DicomViewer from "@/views/DicomViewer.vue";
import Logout from "@/views/user/Logout.vue";
import Register from "@/views/user/Register.vue";
import UserLayout from "@/components/layouts/UserLayout.vue";
import ViewReceiptDisplay from "@/views/ViewReceiptDisplay.vue";
import DefaultLayout from "@/components/layouts/DefaultLayout.vue";
import moduleUser from "@/stores/user";
import store from "@/store";
import KtbFastPay from "@/views/KtbFastPay.vue";
import KtbFastPaySuccess from "@/views/KtbFastPaySuccess.vue";
import KtbFastPayFail from "@/views/KtbFastPayFail.vue";
import KtbFastPayCancel from "@/views/KtbFastPayCancel.vue";

import { eventBus } from "@/main";
import { ROLES } from "@/constants/user";
import { PERMISSIONS } from "@/constants/roles";

Vue.use(VueRouter);

// Routes that must never be persisted as the "resume" target.
// These are transient kiosk/utility screens that error or make no sense as a
// landing page when reopened from localStorage.
const NON_RESUMABLE_PATHS = ["/clinic", "/welcome-display", "/sys-maint"];

function shouldRememberRoute(to) {
  return (
    localStorage.getItem("rememberMe") === "true" &&
    to.meta.requiresAuth &&
    !NON_RESUMABLE_PATHS.includes(to.path) &&
    !to.path.includes("ktbfastpay")
  );
}

const routes = [
  {
    path: "/",
    name: "Home",
    redirect: () => {
      // Check if user has access token
      const token = localStorage.getItem("access_token");

      if (!token) {
        // No token - redirect directly to login
        return "/login";
      }

      // Check for saved route
      const rememberMe = localStorage.getItem("rememberMe");
      const savedRoute = localStorage.getItem("lastAuthenticatedRoute");

      if (rememberMe === "true" && savedRoute) {
        // Redirect directly to saved route (1 redirect instead of 2)
        return savedRoute;
      }

      // Default to clinic
      return "/clinic";
    },
    component: Home,
  },

  {
    path: "/login",
    name: "Login",
    meta: { title: "Login" },
    component: Login,
  },
  {
    path: "/welcome-display",
    name: "PatientWelcomeDisplay",
    meta: { title: "Patient Welcome", requiresAuth: true },
    component: () => import("@/views/PatientWelcomeDisplay.vue"),
  },
  {
    path: "/oauth2",
    name: "OAuth2",
    meta: { title: "OAuth2" },
    component: OAuth2,
  },
  {
    path: "/document",
    name: "Document",
    meta: { title: "Document" },
    component: SignDoc,
  },
  {
    path: "/dicom-viewer",
    name: "DicomViewer",
    meta: { title: "DICOM Viewer" },
    component: DicomViewer,
  },
  {
    path: "/logout",
    name: "Logout",
    component: Logout,
  },
  {
    path: "/register",
    name: "Register",
    component: Register,
  },
  {
    path: "/receipt/view",
    name: "ReceiptDisplay",
    component: ViewReceiptDisplay,
    props: route => ({
      hashRef: route.query.ref,
      mode: "copy",
    }),
  },
  {
    path: "/user",
    component: UserLayout,
    redirect: "/user/activate",
    children: [
      {
        path: "/user/activate/:activateKey?",
        name: "UserActivate",
        meta: { title: "User Activate" },
        component: () => import("@/views/user/Activate.vue"),
      },
      {
        path: "/user/forgotpassword",
        name: "ForgotPassword",
        meta: { title: "Forgot Password" },
        component: () => import("@/views/user/ForgotPassword.vue"),
      },
      {
        path: "/user/resetpassword/:resetPasswordKey",
        name: "ResetPassword",
        meta: { title: "Reset Password" },
        component: () => import("@/views/user/ResetPassword.vue"),
      },
    ],
  },
  {
    path: "/clinic",
    component: DefaultLayout,
    children: [
      {
        path: "/clinic",
        name: "Clinic",
        meta: { title: "Clinic", requiresAuth: true },
        component: () => import("@/views/Clinic.vue"),
      },
    ],
  },
  {
    // Personal AI Assistant page (spec §12.2): any authenticated user; content comes from /api/mcp/me.
    path: "/ai-assistant",
    component: DefaultLayout,
    children: [
      {
        path: "/ai-assistant",
        name: "UserAiAssistant",
        meta: { title: "AI Assistant", requiresAuth: true },
        component: () => import("@/views/user/AiAssistant.vue"),
      },
    ],
  },
  {
    path: "/receipt",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("receipt");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/receipt",
        name: "Receipt",
        meta: { title: "Receipt", requiresAuth: true, requiresDataFetch: true },
        component: () => import("@/views/Receipt.vue"),
      },
    ],
  },
  {
    path: "/dfcorrection",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("dfcorrection");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/dfcorrection",
        name: "DFCorrection",
        meta: {
          title: "แก้ไข DF",
          requiresAuth: true,
          requiresDataFetch: true,
          accessRoles: [ "MANAGER" ],
        },
        component: () => import("@/views/DFCorrection.vue"),
      },
    ],
  },
  {
    path: "/queuemanage",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("queuemanage");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/queuemanage",
        name: "QueueManage",
        meta: {
          title: "จัดการคิวการรักษา",
          requiresAuth: true,
          requiresDataFetch: true,
          keepAlive: true,
          accessRoles: [
            ROLES.ASSISTANT,
            ROLES.COUNTER,
            ROLES.MANAGER,
            ROLES.HEADCOUNTER,
          ],
        },
        component: () => import("@/views/QueueManage.vue"),
      },
    ],
  },
  {
    path: "/queuelist",
    component: DefaultLayout,
    redirect: to => {
      return redirectClinicBranch("queuelist");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/queuelist/:param?",
        name: "QueueList",
        meta: {
          title: "ราการนัดหมาย",
          requiresAuth: true,
          requiresDataFetch: true,
          accessRoles: [
            ROLES.ASSISTANT,
            ROLES.COUNTER,
            ROLES.MANAGER,
            ROLES.HEADCOUNTER,
          ],
        },
        component: () => import("@/views/QueueList.vue"),
        props: true,
      },
    ],
  },
  {
    path: "/usermanage",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("usermanage");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/usermanage",
        name: "UserManage",
        meta: {
          title: "จัดการผู้ใช้",
          requiresAuth: true,
          requiresDataFetch: true,
          accessRoles: [ ROLES.MANAGER, ROLES.COUNTER, ROLES.HEADCOUNTER ],
        },
        component: () => import("@/views/UserManage.vue"),
      },
    ],
  },
  {
    path: "/doctormanage",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("doctormanage");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/doctormanage",
        name: "DoctorManage",
        meta: {
          title: "จัดการแพทย์",
          requiresAuth: true,
          requiresDataFetch: true,
          accessRoles: ["MANAGER", "COUNTER", "HEADCOUNTER"],
        },
        component: () => import("@/views/DoctorManage.vue"),
      },
    ],
  },
  {
    path: "/aptlistmanage",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("aptlistmanage");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/aptlistmanage",
        name: "AptListManage",
        meta: {
          title: "จัดการลิสต์นัดหมาย",
          requiresAuth: true,
          requiresDataFetch: true,
          accessRoles: [
            ROLES.COUNTER,
            ROLES.MANAGER,
            ROLES.DOCTOR,
            ROLES.HEADCOUNTER,
          ],
        },
        component: () => import("@/views/AptListManage.vue"),
      },
    ],
  },
  {
    path: "/txlistmanage",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("txlistmanage");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/txlistmanage",
        name: "TxListManage",
        meta: {
          title: "จัดการลิสต์การรักษา",
          requiresAuth: true,
          requiresDataFetch: true,
          accessRoles: [
            ROLES.COUNTER,
            ROLES.MANAGER,
            ROLES.DOCTOR,
            ROLES.HEADCOUNTER,
            ROLES.ROLE_BRANCH_STOCK_MANAGER,
            ROLES.ROLE_STOCK_STAFF,
          ],
        },
        component: () => import("@/views/TxListManage.vue"),
      },
    ],
  },
  {
    path: "/dxlistmanage",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("dxlistmanage");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/dxlistmanage",
        name: "DxListManage",
        meta: {
          title: "จัดการลิสต์วินิจฉัย",
          requiresAuth: true,
          requiresDataFetch: true,
          accessRoles: [
            ROLES.COUNTER,
            ROLES.MANAGER,
            ROLES.DOCTOR,
            ROLES.HEADCOUNTER,
          ],
        },
        component: () => import("@/views/DxListManage.vue"),
      },
    ],
  },
  {
    path: "/itemslistmanage",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("itemslistmanage");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/itemslistmanage",
        name: "ItemsListManage",
        meta: {
          title: "ลิสต์ยา และสินค้า",
          requiresAuth: true,
          requiresDataFetch: true,
          accessRoles: [
            ROLES.COUNTER,
            ROLES.MANAGER,
            ROLES.DOCTOR,
            ROLES.HEADCOUNTER,
            ROLES.ROLE_BRANCH_STOCK_MANAGER,
            ROLES.ROLE_STOCK_STAFF,
          ],
        },
        component: () => import("@/views/ItemsListManage.vue"),
      },
    ],
  },
  {
    path: "/discountmanage",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("discountmanage");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/discountmanage",
        name: "DiscountManagement",
        meta: {
          title: "จัดการส่วนลด",
          requiresAuth: true,
          requiresDataFetch: true,
          accessRoles: [ "MANAGER" ],
        },
        component: () => import("@/views/DiscountManagement.vue"),
      },
    ],
  },
  {
    path: "/vouchermanage",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("vouchermanage");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/vouchermanage",
        name: "VoucherManagement",
        meta: {
          title: "จัดการบัตรกำนัล",
          requiresAuth: true,
          requiresDataFetch: true,
          // requires, NOT accessRoles: accessRoles is checked through
          // getIsAccessMenu, whose name table covers the seven BRANCH roles only,
          // so RDM/C_LEVEL/ACCOUNTANT mapped to undefined and this route bounced
          // the three central roles that voucher.view exists to admit. meta.requires
          // goes through `can`, which reads permissionContext.scopes and handles
          // clinic_wide / branch_subset — the same key the sidebar entry uses.
          // voucher.view, not voucher.manage: a branch MANAGER/HEADCOUNTER must
          // still reach the list; only the create and status controls are
          // manage-level, and VoucherManagement.vue gates those itself.
          requires: PERMISSIONS.VOUCHER_VIEW,
        },
        component: () => import("@/views/VoucherManagement.vue"),
      },
      {
        path: "/:clinicUrl/:branchUrl/vouchermanage/:batchId/report",
        name: "VoucherBatchReport",
        meta: {
          title: "รายงานบัตรกำนัล",
          requiresAuth: true,
          requiresDataFetch: true,
          // Same key as the list it is opened from — the report is read-only, and
          // gating it on voucher.manage would bounce the branch MANAGER who can
          // see the batch row but not mint one.
          requires: PERMISSIONS.VOUCHER_VIEW,
        },
        component: () => import("@/components/reports/VoucherBatchReport.vue"),
      },
    ],
  },
  {
    path: "/tiermanage",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("tiermanage");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/tiermanage",
        name: "TierManagement",
        meta: {
          title: "จัดการระดับลูกค้า",
          requiresAuth: true,
          requiresDataFetch: true,
          // requires, NOT accessRoles: tier.manage is C_LEVEL/ACCOUNTANT —
          // both CENTRAL roles. accessRoles is checked through
          // getIsAccessMenu, whose name table covers the seven BRANCH roles
          // only, so both central roles would map to undefined and this
          // route would bounce every user tier.manage exists to admit — the
          // identical trap the voucher routes above hit first (see that
          // block's comment). meta.requires goes through `can`, which reads
          // permissionContext.scopes and handles clinic_wide / branch_subset.
          requires: PERMISSIONS.TIER_MANAGE,
        },
        component: () => import("@/views/TierManagement.vue"),
      },
      {
        path: "/:clinicUrl/:branchUrl/tierreport",
        name: "TierReport",
        meta: {
          title: "รายงานระดับลูกค้า",
          requiresAuth: true,
          requiresDataFetch: true,
          // report.clinic.view, NOT tier.view: getTierReport authorizes via
          // authByClinicURL, which only inspects clinic_wide/branch_subset
          // scopes, so a branch role can never reach it regardless of
          // tier.view listing it (see the tier.view comment in
          // constants/roles.js PERMISSION_MAP). This is a deliberate
          // controller decision, not a placeholder — do not swap this for
          // PERMISSIONS.TIER_VIEW.
          requires: PERMISSIONS.REPORT_CLINIC_VIEW,
        },
        component: () => import("@/views/TierReport.vue"),
      },
    ],
  },
  {
    path: "/patientgroup",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("patientgroup");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/patientgroup",
        name: "PatientGroupManagement",
        meta: {
          title: "จัดการกลุ่มผู้ป่วย",
          requiresAuth: true,
          requiresDataFetch: true,
          accessRoles: [ "MANAGER" ],
        },
        component: () => import("@/views/PatientGroupManagement.vue"),
      },
    ],
  },
  {
    path: "/labproductmanage",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("labproductmanage");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/labproductmanage",
        name: "LabProductManage",
        meta: {
          title: "รายการแลป",
          requiresAuth: true,
          requiresDataFetch: true,
          accessRoles: [
            ROLES.COUNTER,
            ROLES.MANAGER,
            ROLES.DOCTOR,
            ROLES.HEADCOUNTER,
          ],
        },
        component: () => import("@/views/LabProductManage.vue"),
      },
    ],
  },
  {
    path: "/doctor/dashboard",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("doctor/dashboard");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/doctor/dashboard",
        name: "Dashboard",
        meta: {
          title: "จัดการคิวการรักษา",
          requiresAuth: true,
          requiresDataFetch: true,
          accessRoles: [ ROLES.DOCTOR ],
        },
        component: () => import("@/views/DoctorDashboard.vue"),
      },
    ],
  },
  {
    path: "/doctorsTxQ",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("doctorsTxQ");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/doctorsTxQ",
        name: "DoctorsTreatingQueue",
        meta: {
          title: "คิวรอตรวจแพทย์ทุกท่าน",
          requiresAuth: true,
          requiresDataFetch: true,
          accessRoles: [ ROLES.ASSISTANT ],
        },
        component: () => import("@/views/DoctorsTreatingQueue.vue"),
      },
    ],
  },
  {
    path: "/packagemanage",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("packagemanage");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/packageManage",
        name: "PackageManage",
        meta: {
          title: "จัดการรายการนัดหมาย",
          requiresAuth: true,
          requiresDataFetch: true,
          accessRoles: [
            ROLES.COUNTER,
            ROLES.MANAGER,
            ROLES.DOCTOR,
            ROLES.HEADCOUNTER,
          ],
        },
        component: () => import("@/views/PackageManage.vue"),
      },
    ],
  },
  {
    path: "/receiptmanage",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("receiptmanage");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/receiptmanage",
        name: "ReceiptManage",
        meta: {
          title: "จัดการใบเสร็จ",
          requiresAuth: true,
          requiresDataFetch: true,
          accessRoles: [ ROLES.COUNTER, ROLES.MANAGER, ROLES.HEADCOUNTER ],
        },
        component: () => import("@/views/ReceiptManage.vue"),
      },
    ],
  },
  {
    path: "/labexpense",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("labexpense");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/labexpense",
        name: "LabExpense",
        meta: {
          title: "จัดการค่าแลป",
          requiresAuth: true,
          requiresDataFetch: true,
          accessRoles: [ ROLES.COUNTER, ROLES.MANAGER, ROLES.HEADCOUNTER ],
        },
        component: () => import("@/views/LabExpense.vue"),
      },
    ],
  },

  {
    path: "/report/personal",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("/report/personal");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/report/personal",
        name: "PersonalIncome",
        meta: {
          title: "รายได้แพทย์",
          requiresAuth: true,
          requiresDataFetch: true,
          accessRoles: [
            ROLES.COUNTER,
            ROLES.MANAGER,
            ROLES.DOCTOR,
            ROLES.HEADCOUNTER,
          ],
        },
        component: () => import("@/views/PersonalIncomeReport.vue"),
      },
    ],
  },
  {
    path: "/report/branch",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("/report/branch");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/report/branch",
        name: "BranchIncome",
        meta: {
          title: "รายได้สาขา",
          requiresAuth: true,
          requiresDataFetch: true,
          accessRoles: [
            ROLES.COUNTER,
            ROLES.MANAGER,
            ROLES.DOCTOR,
            ROLES.HEADCOUNTER,
          ],
        },
        component: () => import("@/views/BranchIncomeReport.vue"),
      },
    ],
  },
  {
    path: "/analysis/branch",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("/analysis/branch");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/analysis/branch",
        name: "branchAnalysis",
        meta: {
          title: "วิเคราห์สาขา",
          requiresAuth: true,
          requiresDataFetch: true,
          keepAlive: true,
          accessRoles: [ ROLES.MANAGER, ROLES.HEADCOUNTER ],
        },
        component: () => import("@/views/BranchAnalysis.vue"),
      },
    ],
  },
  {
    path: "/analysis/patient",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("/analysis/patient");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/analysis/patient",
        name: "PatientAnalysis",
        meta: {
          title: "วิเคราะห์ผู้รับบริการ",
          requiresAuth: true,
          requiresDataFetch: true,
          keepAlive: true,
          accessRoles: [ ROLES.MANAGER, ROLES.HEADCOUNTER ],
        },
        component: () => import("@/views/PatientAnalysis.vue"),
      },
    ],
  },
  {
    path: "/filemanage",
    component: DefaultLayout,

    redirect: () => {
      return redirectClinicBranch("filemanage");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/filemanage",
        name: "FileManage",
        meta: {
          title: "จัดการไฟล์",
          requiresAuth: true,
          requiresDataFetch: true,
          accessRoles: [ ROLES.MANAGER, ROLES.HEADCOUNTER ],
        },
        component: () => import("@/views/FileManage.vue"),
      },
    ],
  },
  {
    path: "/setting",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("setting");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/setting",
        name: "Setting",
        meta: {
          title: "ตั้งค่าสาขา",
          requiresAuth: true,
          requiresDataFetch: true,
          accessRoles: [ ROLES.COUNTER, ROLES.MANAGER, ROLES.HEADCOUNTER ],
        },
        component: () => import("@/views/Setting.vue"),
      },
    ],
  },
  {
    path: "/settings/members",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("settings/members");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/settings/members",
        name: "BranchMembers",
        meta: {
          title: "สมาชิกในสาขา",
          requiresAuth: true,
          requiresDataFetch: true,
          requires: "user.view.branch",
        },
        component: () => import("@/views/settings/BranchMembers.vue"),
      },
    ],
  },
  // HQ stock adjustment approval — clinic-wide, PROCUREMENT role only.
  {
    path: "/:clinicUrl/approval",
    component: DefaultLayout,
    children: [
      {
        path: "",
        name: "HQApproval",
        meta: {
          title: "การอนุมัติ",
          requiresAuth: true,
          requires: PERMISSIONS.STOCK_HQ_APPROVE,
        },
        component: () => import("@/views/product/HQApproval.vue"),
      },
      {
        path: ":branch/adj/:adjustmentId",
        name: "HQApprovalDetail",
        meta: {
          title: "รายละเอียดการจัดการสต๊อก",
          requiresAuth: true,
          requires: PERMISSIONS.STOCK_HQ_APPROVE,
        },
        component: () => import("@/views/product/HQApprovalDetail.vue"),
      },
      {
        path: ":branch/gr/:grId",
        name: "HQApprovalGRDetail",
        meta: {
          title: "รายละเอียดใบรับสินค้า",
          requiresAuth: true,
          requires: PERMISSIONS.STOCK_HQ_APPROVE,
        },
        component: () => import("@/views/product/HQApprovalGRDetail.vue"),
      },
    ],
  },
  // Clinic-wide (HQ) export reports — clinic-wide, PROCUREMENT role only.
  {
    path: "/:clinicUrl/report-download",
    component: DefaultLayout,
    children: [
      {
        path: "",
        name: "ReportDownloadHQ",
        meta: {
          title: "การดาวน์โหลดรายงาน",
          requiresAuth: true,
          requires: PERMISSIONS.STOCK_EXPORT_REPORT_HQ,
        },
        component: () => import("@/views/product/ReportDownloadHQ.vue"),
      },
    ],
  },
  // Accountant (ACCOUNTANT role, clinic-wide): assign a Cost G/L to the master
  // treatments / labs / medicine & products so they become พร้อมใช้งาน.
  // One component; meta.entity selects the master (see constants/costGl).
  {
    path: "/:clinicUrl/accounting",
    component: DefaultLayout,
    children: [
      {
        path: "treatments",
        name: "CostGlTreat",
        meta: {
          title: "การบัญชี - การรักษา",
          requiresAuth: true,
          requires: PERMISSIONS.COST_GL_VIEW,
          entity: "treat",
        },
        component: () => import("@/views/accounting/CostGlList.vue"),
      },
      {
        path: "labs",
        name: "CostGlLab",
        meta: {
          title: "การบัญชี - งานแลป",
          requiresAuth: true,
          requires: PERMISSIONS.COST_GL_VIEW,
          entity: "lab",
        },
        component: () => import("@/views/accounting/CostGlList.vue"),
      },
      {
        path: "items",
        name: "CostGlItem",
        meta: {
          title: "การบัญชี - ยาและสินค้า",
          requiresAuth: true,
          requires: PERMISSIONS.COST_GL_VIEW,
          entity: "item",
        },
        component: () => import("@/views/accounting/CostGlList.vue"),
      },
    ],
  },
  // Phase 1C: clinic-wide central page (no branchUrl) — tabbed user management.
  // MUST be declared before the generic "/:clinicUrl/:branchUrl/dashboard"
  // (BranchDashboard) route below: vue-router 3 matches by definition order, so
  // if BranchDashboard came first, "/:clinicUrl/central/dashboard" would match it
  // with branchUrl="central" — rendering the wrong component and firing
  // branch-scoped data fetches against a non-existent "central" branch (403s).
  {
    path: "/:clinicUrl/central",
    component: DefaultLayout,
    children: [
      {
        // Permission-aware landing: redirects to ClinicDashboard for users who
        // can view it (C_LEVEL), otherwise ClinicManagement (RDM). No `requires`
        // gate here — the dispatcher decides where to send the user.
        path: "",
        name: "ClinicCentral",
        meta: {
          title: "คลินิก",
          requiresAuth: true,
        },
        component: () => import("@/views/central/CentralEntry.vue"),
      },
      {
        path: "users",
        name: "ClinicManagement",
        meta: {
          title: "ผู้ใช้และสิทธิ์",
          requiresAuth: true,
          requires: PERMISSIONS.USER_VIEW_CLINIC,
        },
        component: () => import("@/views/central/ClinicUserManagement.vue"),
      },
      {
        path: "dashboard",
        name: "ClinicDashboard",
        meta: {
          title: "ภาพรวมคลินิก",
          requiresAuth: true,
          requires: PERMISSIONS.DASHBOARD_CLINIC_VIEW,
        },
        component: () => import("@/views/central/ClinicDashboard.vue"),
      },
      {
        path: "dashboard/:branchUrl",
        name: "CentralBranchDashboard",
        meta: {
          title: "แดชบอร์ดสาขา",
          requiresAuth: true,
          requires: PERMISSIONS.DASHBOARD_CLINIC_VIEW,
        },
        component: () => import("@/views/BranchDashboard.vue"),
      },
      {
        path: "report",
        name: "ClinicIncome",
        meta: {
          title: "รายได้คลินิก",
          requiresAuth: true,
          requires: PERMISSIONS.REPORT_CLINIC_VIEW,
        },
        component: () => import("@/views/ClinicIncomeReport.vue"),
      },
      {
        path: "ai-assistant",
        name: "ClinicAiAssistant",
        meta: {
          title: "AI Assistant",
          requiresAuth: true,
          requires: PERMISSIONS.MCP_SETTING_MANAGE,
        },
        component: () => import("@/views/central/ClinicAiAssistant.vue"),
      },
    ],
  },
  {
    path: "/dashboard",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("dashboard");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/dashboard",
        name: "BranchDashboard",
        meta: {
          title: "แดชบอร์ดสาขา",
          requiresAuth: true,
          requiresDataFetch: true,
          accessRoles: [ ROLES.MANAGER ],
        },
        component: () => import("@/views/BranchDashboard.vue"),
      },
    ],
  },
  {
    path: "/subscription",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("subscription");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/subscription",
        name: "Subscription",
        meta: {
          title: "การสมัครบริการ",
          requiresAuth: true,
          requiresDataFetch: true,
          accessRoles: [ ROLES.MANAGER, ROLES.HEADCOUNTER ],
        },
        component: () => import("@/views/Subscription.vue"),
      },
    ],
  },
  {
    path: "/:clinicUrl/:branchUrl/ktbfastpay",
    name: "KtbFastPay",
    meta: {
      title: "KTB Fast Pay",
      requiresAuth: true,
      requiresDataFetch: true,
      accessRoles: [ ROLES.COUNTER, ROLES.MANAGER, ROLES.HEADCOUNTER ],
    },
    component: KtbFastPay,
  },
  {
    path: "/:clinicUrl/:branchUrl/ktbfastpay/success",
    name: "KtbFastPaySuccess",
    meta: {
      title: "KTB Fast Pay - Success",
      requiresAuth: true,
    },
    component: KtbFastPaySuccess,
  },
  {
    path: "/:clinicUrl/:branchUrl/ktbfastpay/fail",
    name: "KtbFastPayFail",
    meta: {
      title: "KTB Fast Pay - Failed",
      requiresAuth: true,
    },
    component: KtbFastPayFail,
  },
  {
    path: "/:clinicUrl/:branchUrl/ktbfastpay/cancel",
    name: "KtbFastPayCancel",
    meta: {
      title: "KTB Fast Pay - Cancelled",
      requiresAuth: true,
    },
    component: KtbFastPayCancel,
  },
  {
    path: "/unitmaster",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("unitmaster");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/unitMaster",
        name: "UnitMaster",
        meta: {
          title: "ตั้งค่าหน่วยสินค้า",
          requiresAuth: true,
          accessRoles: [
            ROLES.ROLE_BRANCH_STOCK_MANAGER,
            ROLES.ROLE_STOCK_STAFF,
          ],
        },
        component: () => import("@/views/product/UnitMaster.vue"),
      },
    ],
  },
  {
    path: "/categorymaster",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("categorymaster");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/categoryMaster",
        name: "CategoryMaster",
        meta: {
          title: "ตั้งค่าหมวดหมู่สินค้า",
          requiresAuth: true,
          accessRoles: [
            ROLES.ROLE_BRANCH_STOCK_MANAGER,
            ROLES.ROLE_STOCK_STAFF,
          ],
        },
        component: () => import("@/views/product/CategoryMaster.vue"),
      },
    ],
  },
  {
    path: "/productinventory",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("productinventory");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/productInventory",
        name: "ProductInventory",
        meta: {
          title: "สินค้าและสต๊อก",
          requiresAuth: true,
          accessRoles: [
            ROLES.ROLE_BRANCH_STOCK_MANAGER,
            ROLES.ROLE_STOCK_STAFF,
          ],
        },
        component: () => import("@/views/product/ProductInventory.vue"),
      },
    ],
  },
  {
    path: "/transferinventory",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("transferinventory");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/transferInventory",
        name: "TransferInventory",
        meta: {
          title: "ใบโอนสินค้า",
          requiresAuth: true,
          accessRoles: [
            ROLES.ROLE_BRANCH_STOCK_MANAGER,
            ROLES.ROLE_STOCK_STAFF,
          ],
        },
        component: () => import("@/views/product/TransferInventory.vue"),
      },
    ],
  },
  {
    path: "/goodsreceive",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("goodsreceive");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/goodsReceive",
        name: "GoodsReceive",
        meta: {
          title: "ใบรับสินค้า",
          requiresAuth: true,
          accessRoles: [
            ROLES.ROLE_BRANCH_STOCK_MANAGER,
            ROLES.ROLE_STOCK_STAFF,
          ],
        },
        component: () => import("@/views/product/GoodsReceive.vue"),
      },
      {
        path: "/:clinicUrl/:branchUrl/goodsReceive/:grId",
        name: "GoodsReceiveDetail",
        meta: {
          title: "รายละเอียดใบรับสินค้า",
          requiresAuth: true,
          accessRoles: [
            ROLES.ROLE_BRANCH_STOCK_MANAGER,
            ROLES.ROLE_STOCK_STAFF,
          ],
        },
        component: () => import("@/views/product/GoodsReceiveDetail.vue"),
      },
    ],
  },
  {
    path: "/purchase-request",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("purchase-request");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/purchase-request",
        name: "PurchaseRequest",
        meta: {
          title: "ใบขอซื้อสินค้า",
          requiresAuth: true,
          accessRoles: [
            ROLES.ROLE_BRANCH_STOCK_MANAGER,
            ROLES.ROLE_STOCK_STAFF,
          ],
        },
        component: () => import("@/views/product/PurchaseRequest.vue"),
      },
      {
        path: "/:clinicUrl/:branchUrl/purchase-request/:prId",
        name: "PurchaseRequestDetail",
        meta: {
          title: "รายละเอียดใบขอซื้อ",
          requiresAuth: true,
          accessRoles: [
            ROLES.ROLE_BRANCH_STOCK_MANAGER,
            ROLES.ROLE_STOCK_STAFF,
          ],
        },
        component: () => import("@/views/product/PurchaseRequestDetail.vue"),
      },
    ],
  },
  {
    path: "/purchase-order",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("purchase-order");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/purchase-order",
        name: "PurchaseOrder",
        meta: {
          title: "ใบสั่งซื้อสินค้า",
          requiresAuth: true,
          accessRoles: [
            ROLES.ROLE_BRANCH_STOCK_MANAGER,
            ROLES.ROLE_STOCK_STAFF,
          ],
        },
        component: () => import("@/views/product/PurchaseOrder.vue"),
      },
      {
        path: "/:clinicUrl/:branchUrl/purchase-order/:id",
        name: "PurchaseOrderDetail",
        meta: {
          title: "รายละเอียดใบสั่งซื้อ",
          requiresAuth: true,
          accessRoles: [ "ROLE_BRANCH_STOCK_MANAGER", "ROLE_STOCK_STAFF" ],
        },
        component: () => import("@/views/product/PurchaseOrderDetail.vue"),
      },
    ],
  },
  {
    path: "/stockManagement",
    component: DefaultLayout,
    redirect: () => {
      return redirectClinicBranch("stockManagement");
    },
    children: [
      {
        path: "/:clinicUrl/:branchUrl/stockManagement",
        name: "StockManagement",
        meta: {
          title: "จัดการสต๊อก",
          requiresAuth: true,
          accessRoles: [
            ROLES.ROLE_BRANCH_STOCK_MANAGER,
            ROLES.ROLE_STOCK_STAFF,
          ],
        },
        component: () => import("@/views/product/StockManagement.vue"),
      },
      {
        path: "/:clinicUrl/:branchUrl/stockManagement/:adjustmentId",
        name: "StockManagementDetail",
        meta: {
          title: "การจัดการสต๊อก",
          requiresAuth: true,
          requires: PERMISSIONS.STOCK_VIEW_DETAIL,
        },
        component: () => import("@/views/product/StockManagementDetail.vue"),
      },
    ],
  },
];

// Add non-production routes (local, dev, staging, gamma)
if (
  process.env.VUE_APP_MODE === "dev" ||
  process.env.VUE_APP_MODE === "staging" ||
  process.env.VUE_APP_MODE === "gamma"
) {
  // System Maintenance Route - Available in dev, staging, gamma only
  routes.push({
    path: "/sys-maint",
    name: "SysMaintenanceDashboard",
    component: UserLayout,
    children: [
      {
        path: "/sys-maint",
        component: () =>
          import(
            /* webpackChunkName: "sys-maint" */ "@/views/superadmin/SysMaintenanceDashboard.vue"
          ),
        meta: {
          title: "System Maintenance",
          requiresAuth: true,
          requiresSuperadmin: true,
        },
      },
    ],
  });
}

// Add development-only routes
if (
  process.env.NODE_ENV === "development" ||
  process.env.NODE_ENV === "dev" ||
  process.env.VUE_APP_MODE === "dev"
) {
  routes.push({
    path: "/dev/error-testing",
    name: "ErrorTesting",
    meta: { title: "Error Testing - Development Only" },
    component: () =>
      import(
        /* webpackChunkName: "dev-error-testing" */ "@/views/dev/ErrorTestingPage.vue"
      ),
  });
}

// Wildcard route must be last
routes.push({
  path: "*",
  component: () =>
    import(/* webpackChunkName: "errors-404" */ "@/views/error/NotFound.vue"),
});

const router = new VueRouter({
  mode: "history",
  linkActiveClass: "active",
  routes,
});

function redirectClinicBranch(to) {
  const branchPath = moduleUser.state.branchPath;
  if (branchPath) {
    return `/${branchPath}/${to}`;
  } else {
    return "/clinic";
  }
}

async function fetchData(to) {
  if (!to?.params) {
    throw new Error("Invalid route parameters");
  }

  const { clinicUrl, branchUrl } = to.params;
  const branchKey = `${clinicUrl}/${branchUrl}`;
  const now = Date.now();

  // Check if we're already fetching data
  if (isFetchingData && pendingDataFetch) {
    try {
      return await pendingDataFetch;
    } catch (error) {
      // If pending fetch failed, continue with new attempt
    }
  }

  // Check cooldown period for this specific clinic/branch
  const lastFetch = dataFetchHistory.get(branchKey);
  if (
    lastFetch &&
    lastFetch.attempts < MAX_FETCH_ATTEMPTS &&
    now - lastFetch.timestamp < DATA_FETCH_COOLDOWN
  ) {
    // Skip fetching if we're in cooldown period
    return;
  }

  // Reset attempts if cooldown period has passed
  if (lastFetch && now - lastFetch.timestamp >= DATA_FETCH_COOLDOWN) {
    dataFetchHistory.set(branchKey, { timestamp: now, attempts: 0 });
  }

  // Track fetch attempts to prevent infinite loops
  const currentAttempts = (lastFetch?.attempts || 0) + 1;
  if (currentAttempts > MAX_FETCH_ATTEMPTS) {
    return;
  }

  // Mark as fetching and create promise for deduplication
  isFetchingData = true;
  dataFetchHistory.set(branchKey, {
    timestamp: now,
    attempts: currentAttempts,
  });

  pendingDataFetch = (async () => {
    try {
      eventBus.$emit("appLoadingStatus", true);

      // Critical sequential data that must be fetched in order
      try {
        await store.dispatch(
          "moduleAppointment/fetchTomorrowAppointmentList",
          to.params
        );
      } catch (error) {
        console.error("Failed to fetch tomorrow appointments:", error);
        // Continue with other fetches even if this fails
      }

      try {
        await store.dispatch(
          "moduleTreatment/fetchOrthoPackageList",
          to.params
        );
      } catch (error) {
        console.error("Failed to fetch ortho packages:", error);
        // Continue with other fetches even if this fails
      }

      // Non-critical data that can be fetched in parallel
      const parallelFetches = [
        {
          name: "Branch Members",
          action: "moduleUser/fetchAllBranchMembers",
        },
        {
          name: "Appointment Categories",
          action: "moduleAppointment/fetchAppointmentCateAndList",
        },
        {
          name: "Diagnosis Categories",
          action: "moduleAppointment/fetchDiagnosisCateAndList",
        },
        {
          name: "Treatment Categories",
          action: "moduleAppointment/fetchTreatmentCateAndList",
        },
        {
          name: "Item Categories",
          action: "moduleAppointment/fetchItemCateAndList",
        },
      ];

      // Use Promise.allSettled to prevent one failure from stopping others
      const results = await Promise.allSettled(
        parallelFetches.map(({ name, action }) =>
          store.dispatch(action, to.params).catch(error => {
            console.error(`Failed to fetch ${name} (${action}):`, error);
            // Don't re-throw here, let Promise.allSettled handle it
            return null;
          })
        )
      );

      // Log any rejections but don't fail the entire process
      results.forEach((result, index) => {
        if (result.status === "rejected") {
          console.error(
            `Parallel fetch ${parallelFetches[ index ].name} failed:`,
            result.reason
          );
        }
      });

      // Reset attempts on successful completion
      dataFetchHistory.set(branchKey, { timestamp: now, attempts: 0 });
    } catch (error) {
      console.error("Data fetching failed:", error);
      // Don't throw here to prevent cascading failures
    } finally {
      eventBus.$emit("appLoadingStatus", false);
      isFetchingData = false;
      pendingDataFetch = null;
    }
  })();

  return pendingDataFetch;
}

let dataFetched = false;
let pendingLoginCheck = null;
const routeHistory = [];
const MAX_REDIRECTS = 3;

// Data fetching deduplication and state management
let isFetchingData = false;
let pendingDataFetch = null;
const dataFetchHistory = new Map(); // Track fetches per clinic/branch
const DATA_FETCH_COOLDOWN = 30000; // 30 seconds
const MAX_FETCH_ATTEMPTS = 3;

// Phase 1C: meta.requires permission gate.
// `to.meta.requires` is a permission key (e.g. 'user.view.clinic'). When set,
// resolves the route's clinicId from the loaded branchInfo and calls the `can`
// getter. Returns true if allowed (or if no `requires` set).
const passesPermissionGate = (to) => {
  if (!to.meta.requires) return true;
  const userState = moduleUser.state;
  if (!userState.permissionContext) {
    // No context loaded yet — let the route render; the page itself will trigger
    // a refresh via the 403 handler if the BE rejects the request.
    return true;
  }
  const ctx = {};
  // Resolve clinicId from loaded branchInfo when clinicUrl matches the param.
  if (to.params.clinicUrl && userState.branchInfo?.clinic?.code === to.params.clinicUrl) {
    ctx.clinicId = userState.branchInfo.clinic.id;
  }
  // Resolve branchId when the route is branch-scoped and matches loaded branch.
  if (to.params.branchUrl && userState.branchInfo?.code === to.params.branchUrl) {
    ctx.branchId = userState.branchInfo.id;
  }
  return moduleUser.getters.can(userState)(to.meta.requires, ctx);
};

// Phase 1C: kick off loading the permission context for the route's clinic.
// Fire-and-forget (intentionally not awaited by callers) so navigation isn't
// blocked on the network — `can`/`$can` (sidebar menus, permission-gated UI)
// update reactively once it resolves. Without this, a hard refresh or direct
// navigation to a central route (which never passes through CentralEntry) would
// leave the context null and those checks stuck at false even for users who do
// have access. ensurePermissionContextFor is a no-op once the context is loaded
// for the clinic, so this only hits the network on first entry / clinic switch.
const ensureClinicPermissionContext = async (to) => {
  const clinicURL = to.params.clinicUrl;
  if (!clinicURL) return;
  try {
    await store.dispatch("moduleUser/ensurePermissionContextFor", { clinicURL });
  } catch (e) {
    // Non-fatal: the 403 handler will refresh the context on demand.
  }
};

// Function to check if cache is truly valid
const isCacheValid = (state, now) => {
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  return (
    state.lastCheckTime &&
    state.userInfo &&
    state.branchInfo &&
    state.userInfo.uid && // Ensure user data is complete
    state.branchInfo.id && // Ensure branch data is complete
    now - state.lastCheckTime < CACHE_DURATION
  );
};

router.beforeEach(async (to, from, next) => {
  // Prevent redirect loops
  routeHistory.push(to.path);
  if (routeHistory.length > MAX_REDIRECTS) {
    const recentRoutes = routeHistory.slice(-MAX_REDIRECTS);
    // Check for same route repeated
    const sameRouteLoop = recentRoutes.every(route => route === to.path);
    // Check for clinic-specific loops
    const clinicLoopCount = recentRoutes.filter(
      route => route === "/clinic"
    ).length;

    if (sameRouteLoop || clinicLoopCount >= 2) {
      routeHistory.length = 0; // Clear history
      pendingLoginCheck = null; // Clear any pending requests
      next({ path: "/login", replace: true });
      return;
    }
  }

  // Keep history manageable
  if (routeHistory.length > 10) {
    routeHistory.splice(0, 5);
  }

  let publicPageNames = [
    "Login",
    "ForgotPassword",
    "ResetPassword",
    "OAuth2",
    "edcweb",
    "ReceiptDisplay",
  ];
  if (publicPageNames.includes(to.name)) {
    NProgress.start();
    next();
    return;
  }

  if (to.name === "Clinic") {
    dataFetched = false; //Fetch new data when branch changed
    // Clear data fetch history for new clinic to allow immediate fetch
    const { clinicUrl, branchUrl } = to.params;
    if (clinicUrl && branchUrl) {
      const branchKey = `${clinicUrl}/${branchUrl}`;
      dataFetchHistory.delete(branchKey);
    }
  }

  if (to.meta.requiresAuth == true) {
    // Check if we have valid cached user data to skip unnecessary API calls
    const now = Date.now();
    const hasValidCache = isCacheValid(moduleUser.state, now);

    if (hasValidCache) {
      // Kick off loading the clinic permission context (non-blocking) so the
      // sidebar `can` checks resolve; navigation proceeds immediately.
      ensureClinicPermissionContext(to);

      // Use cached data for access control check
      const isAccess = moduleUser.getters.getIsAccessMenu(moduleUser.state)(
        to.meta.accessRoles
      );
      if (to.meta.accessRoles && to.meta.accessRoles.length > 0 && !isAccess) {
        // Avoid redirecting to /clinic if we're already there to prevent loops
        if (to.path === "/clinic") {
          next({ path: "/login" });
        } else {
          next({ path: "/clinic" });
        }
      } else if (!passesPermissionGate(to)) {
        // Phase 1C: meta.requires permission key didn't pass.
        next({ path: "/clinic" });
      } else {
        // Check superadmin permission if required
        if (to.meta.requiresSuperadmin) {
          try {
            const isSuperadmin = await store.dispatch(
              "moduleSuperadmin/checkSuperadminAccess"
            );
            if (!isSuperadmin) {
              // Redirect to login if not superadmin
              next({ path: "/login" });
              return;
            }
          } catch (error) {
            console.error("Superadmin access check failed:", error);
            next({ path: "/login" });
            return;
          }
        }

        // Clear saved route on successful initial load navigation
        if (!from.name && localStorage.getItem("lastAuthenticatedRoute")) {
          localStorage.removeItem("lastAuthenticatedRoute");
        }

        // Clear saved route when user manually navigates to /clinic
        if (to.name === "Clinic" && from.name) {
          localStorage.removeItem("lastAuthenticatedRoute");
        }

        NProgress.start();
        if (to.meta.requiresDataFetch && !dataFetched) {
          try {
            await fetchData(to);
            dataFetched = true;
          } catch (error) {
            console.error("Failed to fetch route data:", error);
            // Continue navigation even if data fetch fails
          }
        }

        // Save authenticated route to localStorage if rememberMe is enabled
        if (shouldRememberRoute(to)) {
          localStorage.setItem("lastAuthenticatedRoute", to.fullPath);
        }

        next();
      }
    } else {
      // Cache is stale or missing, call the API with deduplication
      try {
        if (pendingLoginCheck) {
          // Wait for existing check instead of creating new one
          await pendingLoginCheck;
        } else {
          // Create new login check
          pendingLoginCheck = store.dispatch(
            "moduleUser/newCheckUserLogin",
            to.params
          );
          await pendingLoginCheck;
          pendingLoginCheck = null;
        }

        // Kick off loading the clinic permission context (non-blocking) so the
        // sidebar `can` checks resolve; navigation proceeds immediately.
        ensureClinicPermissionContext(to);

        // After successful login check
        const isAccess = moduleUser.getters.getIsAccessMenu(moduleUser.state)(
          to.meta.accessRoles
        );
        if (
          to.meta.accessRoles &&
          to.meta.accessRoles.length > 0 &&
          !isAccess
        ) {
          // Avoid redirecting to /clinic if we're already there to prevent loops
          if (to.path === "/clinic") {
            next({ path: "/login" });
          } else {
            next({ path: "/clinic" });
          }
        } else {
          // Check superadmin permission if required
          if (to.meta.requiresSuperadmin) {
            try {
              const isSuperadmin = await store.dispatch(
                "moduleSuperadmin/checkSuperadminAccess"
              );
              if (!isSuperadmin) {
                // Redirect to login if not superadmin
                next({ path: "/login" });
                return;
              }
            } catch (error) {
              console.error("Superadmin access check failed:", error);
              next({ path: "/login" });
              return;
            }
          }

          // Clear saved route on successful initial load navigation
          if (!from.name && localStorage.getItem("lastAuthenticatedRoute")) {
            localStorage.removeItem("lastAuthenticatedRoute");
          }

          // Clear saved route when user manually navigates to /clinic
          if (to.name === "Clinic" && from.name) {
            localStorage.removeItem("lastAuthenticatedRoute");
          }

          NProgress.start();
          if (to.meta.requiresDataFetch && !dataFetched) {
            try {
              await fetchData(to);
              dataFetched = true;
            } catch (error) {
              console.error("Failed to fetch route data:", error);
              // Continue navigation even if data fetch fails
            }
          }

          // Save authenticated route to localStorage if rememberMe is enabled
          if (shouldRememberRoute(to)) {
            localStorage.setItem("lastAuthenticatedRoute", to.fullPath);
          }

          next();
        }
      } catch (_err) {
        pendingLoginCheck = null; // Clear pending request on error
        next({ path: "/login" });
      }
    }
  } else {
    NProgress.start();
    next();
  }
});

router.afterEach(async (to, from) => {
  NProgress.done();
  // A settled navigation is, by definition, not a redirect loop (loops abort
  // each hop via next({ path }) and never reach afterEach). Reset the loop
  // counter so legitimate revisits (e.g. logo → /clinic after entering a
  // branch) don't accumulate into a false positive and bounce to /login.
  routeHistory.length = 0;
});

export default router;
